import React from 'react';
import { Outlet, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { isSiteOwnerEmail } from '../services/api';
import { blogService } from '../services/blogService';
import NotificationBell from './NotificationBell';
import ChatBadge from './ChatBadge';
import CookieBanner from './CookieBanner';
import FeedbackDialog from './FeedbackDialog';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Event,
  Add,
  Person,
  Logout,
  Login,
  Chat as ChatIcon,
  AdminPanelSettings as AdminIcon,
  Home as HomeIcon,
  Article as ArticleIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const navPages = [
  { name: 'Мероприятия', path: '/events', icon: <Event /> },
  { name: 'Чаты', path: '/chats', icon: <ChatIcon /> },
  { name: 'Блог', path: '/blog', icon: <ArticleIcon /> },
  { name: 'Новости', path: '/news', icon: <ArticleIcon /> },
];

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout, setLoginModalOpen } = useAuthStore();

  const isChatPage = location.pathname.startsWith('/chats');
  const isHomePage = location.pathname === '/';

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [hasArticles, setHasArticles] = React.useState(false);

  const isFullAdmin = user?.role === 'site_admin' || isSiteOwnerEmail(user?.email);
  // Показывать «Админ-панель» админам и авторам статей/черновиков
  React.useEffect(() => {
    if (!isAuthenticated) { setHasArticles(false); return; }
    if (hasArticles) return;
    blogService.hasMyArticles().then(setHasArticles).catch(() => {});
  }, [isAuthenticated, location.pathname]);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    handleCloseUserMenu();
    navigate('/');
  };

  const getBottomNavValue = () => {
    if (location.pathname === '/') return 0;
    if (location.pathname.startsWith('/events')) return 1;
    if (location.pathname.startsWith('/chats')) return 2;
    if (location.pathname.startsWith('/profile') || location.pathname.startsWith('/my-data')) return 3;
    return -1;
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 2 }}>
        <Box component={RouterLink} to="/" sx={{ textDecoration: 'none', color: 'inherit' }}>
          <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
            Nashrelax<Box component="span" sx={{ color: '#ef6c00' }}>.ru</Box>
          </Typography>
        </Box>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <List sx={{ flex: 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            component={RouterLink}
            to="/"
            selected={location.pathname === '/'}
            onClick={handleDrawerToggle}
          >
            <ListItemIcon><HomeIcon /></ListItemIcon>
            <ListItemText primary="Главная" />
          </ListItemButton>
        </ListItem>
        {navPages.map((page) => (
          <ListItem key={page.name} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={page.path}
              selected={location.pathname === page.path}
              onClick={handleDrawerToggle}
            >
              <ListItemIcon>{page.icon}</ListItemIcon>
              <ListItemText primary={page.name} />
            </ListItemButton>
          </ListItem>
        ))}
        {isAuthenticated && (
          <ListItem disablePadding>
            <ListItemButton
              component={RouterLink}
              to="/events/create"
              selected={location.pathname === '/events/create'}
              onClick={handleDrawerToggle}
            >
              <ListItemIcon><Add /></ListItemIcon>
              <ListItemText primary="Создать мероприятие" />
            </ListItemButton>
          </ListItem>
        )}
        {isAuthenticated && (isFullAdmin || user?.role === 'editor' || hasArticles) && (
            <ListItem disablePadding>
              <ListItemButton
                component={RouterLink}
                to="/admin"
                selected={location.pathname === '/admin'}
                onClick={handleDrawerToggle}
              >
                <ListItemIcon><AdminIcon /></ListItemIcon>
                <ListItemText primary="Админ-панель" />
              </ListItemButton>
            </ListItem>
          )}
      </List>
      <Divider />
      <List>
        {isAuthenticated ? (
          <>
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/profile" onClick={handleDrawerToggle}>
                <ListItemIcon><Person /></ListItemIcon>
                <ListItemText primary="Профиль" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => { handleLogout(); handleDrawerToggle(); }}>
                <ListItemIcon><Logout /></ListItemIcon>
                <ListItemText primary="Выход" />
              </ListItemButton>
            </ListItem>
          </>
        ) : (
          <ListItem disablePadding>
            <ListItemButton onClick={() => { setLoginModalOpen(true); handleDrawerToggle(); }}>
              <ListItemIcon><Login /></ListItemIcon>
              <ListItemText primary="Войти" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      {/* Header */}
      <AppBar
        position="sticky"
        sx={{
          bgcolor: '#ffffff',
          color: '#1e293b',
          borderBottom: '2px solid #1565c0',
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar sx={{ px: { xs: 1.5, sm: 2 }, minHeight: { xs: 52, sm: 64 } }}>
            {/* Mobile menu button */}
            <IconButton
              size="small"
              aria-label="menu"
              onClick={handleDrawerToggle}
              sx={{ mr: 1, display: { xs: 'flex', md: 'none' }, color: '#1e293b' }}
            >
              <MenuIcon />
            </IconButton>

            {/* Logo */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: 'inherit',
                mr: { xs: 1, md: 3 },
                flexShrink: 0,
              }}
            >
              <Typography
                variant="h6"
                noWrap
                fontWeight={900}
                sx={{
                  fontSize: { xs: '1.1rem', sm: '1.3rem' },
                  letterSpacing: '-0.03em',
                  color: '#1e293b',
                }}
              >
                Nashrelax
                <Box component="span" sx={{ color: '#ef6c00' }}>.ru</Box>
              </Typography>
            </Box>

            {/* Desktop nav */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 0.5 }}>
              {navPages.map((page) => {
                const isActive = location.pathname === page.path || location.pathname.startsWith(page.path + '/');
                const isChatsPage = page.path === '/chats';
                return (
                  <Button
                    key={page.name}
                    component={RouterLink}
                    to={isChatsPage && !isAuthenticated ? '#' : page.path}
                    onClick={isChatsPage && !isAuthenticated ? (e) => { e.preventDefault(); setLoginModalOpen(true); } : undefined}
                    startIcon={page.icon}
                    sx={{
                      color: isActive ? '#1565c0' : '#1e293b',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      px: 1.5,
                      position: 'relative',
                      '&::after': isActive ? {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '60%',
                        height: 2,
                        bgcolor: '#1565c0',
                        borderRadius: 1,
                      } : {},
                    }}
                  >
                    {page.name}
                  </Button>
                );
              })}
            </Box>

            {/* Right side */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, ml: 'auto' }}>
              {isAuthenticated && user ? (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => navigate('/events/create')}
                    sx={{
                      display: { xs: 'none', sm: 'flex' },
                      bgcolor: '#ef6c00',
                      '&:hover': { bgcolor: '#e65100' },
                      fontSize: '0.8rem',
                      px: 1.5,
                    }}
                  >
                    Создать
                  </Button>
                  <NotificationBell userId={0} />
                  <ChatBadge />
                  <Tooltip title="Профиль">
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0.5 }}>
                      <Avatar
                        alt={user.first_name || 'User'}
                        src={user.avatar_url || undefined}
                        sx={{ width: 34, height: 34, border: '2px solid #e2e8f0' }}
                      >
                        {!user.avatar_url && (user.first_name?.[0] || user.email?.[0] || 'U')}
                      </Avatar>
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setLoginModalOpen(true)}
                  sx={{
                    bgcolor: '#1565c0',
                    '&:hover': { bgcolor: '#0d47a1' },
                    fontSize: '0.8rem',
                    px: 2,
                  }}
                >
                  Войти
                </Button>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          pb: { xs: isChatPage ? 0 : 7, md: 0 },
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            flexGrow: 1,
            py: { xs: isHomePage ? 0 : 2, sm: isHomePage ? 0 : 3 },
            px: { xs: isHomePage ? 0 : 1.5, sm: 2 },
          }}
          disableGutters={isHomePage}
        >
          <Outlet />
        </Container>

        {/* Footer — desktop only or non-mobile */}
        <Box
          component="footer"
          sx={{
            py: 3,
            px: 2,
            textAlign: 'center',
            borderTop: '1px solid #e2e8f0',
            mt: 'auto',
            bgcolor: '#ffffff',
            display: { xs: isChatPage ? 'none' : 'none', sm: 'block' },
          }}
        >
          <Container maxWidth="xl">
            <Typography variant="body2" fontWeight={700} sx={{ mb: 1.5 }}>
              Nashrelax
              <Box component="span" sx={{ color: '#ef6c00' }}>.ru</Box>
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <RouterLink to="/privacy-policy" style={{ color: '#64748b', textDecoration: 'none', fontSize: '0.75rem' }}>
                Политика конфиденциальности
              </RouterLink>
              <RouterLink to="/terms-of-service" style={{ color: '#64748b', textDecoration: 'none', fontSize: '0.75rem' }}>
                Пользовательское соглашение
              </RouterLink>
              <RouterLink to="/community-rules" style={{ color: '#64748b', textDecoration: 'none', fontSize: '0.75rem' }}>
                Правила сообщества
              </RouterLink>
            </Box>
            <Button
              variant="text"
              size="small"
              onClick={() => setFeedbackOpen(true)}
              sx={{ color: '#999', fontSize: '0.75rem' }}
            >
              Предложения по развитию
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1, color: '#bbb' }}>
              &copy; 2026 Nashrelax.ru
            </Typography>
          </Container>
        </Box>
      </Box>

      {/* Mobile bottom navigation */}
      {!isChatPage && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            display: { xs: 'block', md: 'none' },
            zIndex: 1100,
            borderTop: '1px solid #e2e8f0',
            borderRadius: 0,
          }}
          elevation={0}
        >
          <BottomNavigation
            value={getBottomNavValue()}
            sx={{
              height: 56,
              bgcolor: '#ffffff',
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                py: 0.5,
                '&.Mui-selected': {
                  color: '#1565c0',
                },
              },
            }}
          >
            <BottomNavigationAction
              label="Главная"
              icon={<HomeIcon />}
              onClick={() => navigate('/')}
            />
            <BottomNavigationAction
              label="Мероприятия"
              icon={<Event />}
              onClick={() => navigate('/events')}
            />
            <BottomNavigationAction
              label="Чаты"
              icon={<ChatIcon />}
              onClick={() => {
                if (!isAuthenticated) { setLoginModalOpen(true); return; }
                navigate('/chats');
              }}
            />
            <BottomNavigationAction
              label="Профиль"
              icon={<Person />}
              onClick={() => {
                if (!isAuthenticated) { setLoginModalOpen(true); return; }
                navigate('/profile');
              }}
            />
          </BottomNavigation>
        </Paper>
      )}

      {/* Feedback Dialog */}
      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

      {/* User menu */}
      <Menu
        sx={{ mt: '45px' }}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
      >
        {isAuthenticated && user && (
          <>
            <MenuItem component={RouterLink} to="/profile" onClick={handleCloseUserMenu}>
              <ListItemIcon><Person fontSize="small" /></ListItemIcon>
              Профиль
            </MenuItem>
            <MenuItem component={RouterLink} to="/my-data" onClick={handleCloseUserMenu}>
              <ListItemIcon><Event fontSize="small" /></ListItemIcon>
              Мои данные
            </MenuItem>
            {user && (isFullAdmin || user.role === 'editor' || hasArticles) && (
              <MenuItem component={RouterLink} to="/admin" onClick={handleCloseUserMenu}>
                <ListItemIcon><AdminIcon fontSize="small" /></ListItemIcon>
                Админ-панель
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
              Выйти
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Cookie Banner */}
      <CookieBanner />
    </Box>
  );
};

export default Layout;